import { FC, ReactNode, useMemo, Fragment } from 'react';

import { Button, Divider, Grid, Link, Typography } from '@mui/material';
import { FieldArray, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { LoanType } from 'src/api/zrm';
import FlexSpaceBetweenBox from 'src/components/boxes/FlexSpaceBetweenBox';
import DevTools from 'src/components/DevTools/DevTools';
import LoanInterestRateField from 'src/components/fields/Loans/LoanInterestRateField';
import LoanMonthlyPaymentField from 'src/components/fields/Loans/LoanMonthlyPaymentField';
import LoanQuestionField from 'src/components/fields/Loans/LoanQuestionField';
import LoanResponsibilityField from 'src/components/fields/Loans/LoanResponsibilityField';
import LoanTotalAmountField from 'src/components/fields/Loans/LoanTotalAmountField';
import NextStepButton from 'src/components/Stepper/common/NextStepButton';
import { FormSectionContextProvider } from 'src/contexts/FormSectionContext';
import { useSharedDataFormContext } from 'src/contexts/shared/SharedDataFormContext';
import { useYupSchemaContext } from 'src/contexts/YupSchemaContext';
import BlancoNorwayForm from 'src/types/BlancoNorway/form/BlancoNorwayForm';
import MortgageNorwayForm from 'src/types/MortgageNorway/form/MortgageNorwayForm';
import SharedNorwayLoanForm, { SharedNorwayLoan } from 'src/types/SharedForm/loans/SharedNorwayLoansForm';
import getListOfFieldToTouch from 'src/utils/formik/getListOfFieldsToTouch';

interface SharedNorwayLoansSectionProps {
  formKey: keyof SharedNorwayLoanForm;
  title: string;
  index: number;
  loanType: LoanType;
  itemTitle: string;
}

const SharedNorwayLoansSection: FC<SharedNorwayLoansSectionProps> = (props) => {
  const { formKey, title, index, loanType, itemTitle } = props;

  const { formData, updateFormSection } = useSharedDataFormContext();
  const { schemaFields } = useYupSchemaContext();
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    hasLoan: schemaFields.securedLoans.hasLoan,
    loans: schemaFields.securedLoans.loans,
  });

  const initialValues = useMemo(() => {
    const data = formData as BlancoNorwayForm | MortgageNorwayForm;
    const d = data.loans[formKey];

    return {
      hasLoan: d?.hasLoan,
      loans: d?.loans,
    };
  }, [formData, formKey]);

  const createNewLoan = (): SharedNorwayLoan => {
    return {
      monthlyPayment: null,
      rate: null,
      totalAmount: null,
      multipleBorrowers: null,
    };
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => { }}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ values, validateForm, setTouched }): ReactNode => (
        <FormSectionContextProvider
          updateFormContextCallback={() => {
            const temp = { ...values, loanType };
            updateFormSection(`loans.${formKey}`, temp);
          }}
          heading={`Debt Information (${index}/3)`}
        >
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
            >
              <LoanQuestionField
                name='hasLoan'
                title={title}
              />
            </Grid>
            {values.hasLoan && (
              <>
                <FieldArray
                  name="loans"
                  render={arrayHelpers => (
                    <>
                      {values.loans && values.loans.length > 0 && (
                        values.loans.map((loan, i) => (
                          <Fragment key={i}>
                            {values.loans.length > 1 && (
                              <Grid
                                item
                                xs={12}
                                className='loan-item-header'
                              >
                                <FlexSpaceBetweenBox>
                                  <Typography sx={{ fontWeight: 'bold' }}>{itemTitle} {i + 1}</Typography>
                                  <Link
                                    component={Button}
                                    onClick={() => arrayHelpers.remove(i)}
                                    color='error'
                                    sx={{ textTransform: 'none' }}
                                  >
                                    - {t('Remove')}
                                  </Link>
                                </FlexSpaceBetweenBox>
                              </Grid>
                            )}
                            <Grid
                              item
                              xs={12}
                            >
                              <LoanTotalAmountField name={`loans.${i}.totalAmount`} />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                            >
                              <LoanInterestRateField name={`loans.${i}.rate`} />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                            >
                              <LoanMonthlyPaymentField name={`loans.${i}.monthlyPayment`} />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                            >
                              <LoanResponsibilityField name={`loans.${i}.multipleBorrowers`} />
                            </Grid>
                            {i < values.loans.length - 1 && (
                              <Grid
                                item
                                xs={12}
                              >
                                <Divider />
                              </Grid>

                            )}
                          </Fragment>
                        )))}
                      <Grid
                        item
                        xs={12}
                      >
                        <Link
                          component={Button}
                          onClick={async () => {
                            const invalidFields = await validateForm();

                            const touched = getListOfFieldToTouch(invalidFields);

                            setTouched(touched);

                            if (Object.keys(invalidFields).length === 0) {
                              arrayHelpers.push(createNewLoan());

                              setTimeout(() => {
                                const temp = document.querySelectorAll('.loan-item-header');
                                const el = temp[temp.length - 1] as HTMLElement;

                                if (el) {
                                  el.scrollIntoView({ behavior: 'smooth' });
                                }
                              }, 100);
                            }
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('Add Loan')} +
                        </Link>
                      </Grid>
                    </>
                  )}
                />
              </>
            )}
            <Grid
              item
              xs={12}
            >
              <NextStepButton alwaysVisible={values.hasLoan} />
            </Grid>
            <DevTools />
          </Grid>
        </FormSectionContextProvider>
      )}
    </Formik>
  );
};

export default SharedNorwayLoansSection;
